/* eslint-disable react-hooks/exhaustive-deps */
import {compose} from 'recompose';
import {withRouter, WithRouterProps} from 'react-router';
import {FormikTextInput, Card, FormikCheckbox, Title} from '@shipwell/shipwell-ui';
import {Field, Formik} from 'formik';
import {object, string} from 'yup';
import {FormEventHandler} from 'react';
import {TransportationMode} from '@shipwell/genesis-sdk';
import {useFlags} from 'launchdarkly-react-client-sdk';
import PageHeader from 'App/common/pageHeader';
import withFlags from 'App/utils/withFlags';
import withConditionalFallback from 'App/components/withConditionalFallback';
import WithStatusToasts, {WithStatusToastProps} from 'App/components/withStatusToasts';
import {useChRobinsonMutation} from 'App/containers/chrobinson/hooks/useChRobinsonMutation';
import FormFooter from 'App/formComponents/formSections/formFooter';

interface CHRPropsTypes extends WithStatusToastProps, WithRouterProps<unknown, unknown> {}

const validationSchema = object().shape({
  c_code: string()
    .required('Account Code is required')
    .matches(/^C\d{5,7}$/, 'C Code format is not correct')
});

/**
 * CHRobinson Registration Container
 */
const CHRobinsonRegistration = (props: CHRPropsTypes) => {
  const {intCarrierApiChrLtl} = useFlags() || {};

  const {mutate: createCapacityProviders} = useChRobinsonMutation({
    onSuccess() {
      props.router.push('/carriers?page=1&q=chrobinson&chrobinsonRegistrationSuccess');
    }
  });

  const onCancel = () => {
    props.router.goBack();
  };

  const handleConnect = (values: {c_code: string; ltl: boolean; ftl: boolean}) => {
    const modes = intCarrierApiChrLtl
      ? ([values.ltl && TransportationMode.Ltl, values.ftl && TransportationMode.Ftl].filter(
          (x) => !!x
        ) as TransportationMode[])
      : [TransportationMode.Ftl];

    createCapacityProviders({cCode: values.c_code, modes});
  };

  const validateCheckboxes = (values: {ltl: boolean; ftl: boolean}) => {
    return !values.ltl && !values.ftl ? 'Select at least one option' : false;
  };

  return (
    <div className="min-h-screen bg-sw-background">
      <PageHeader title="CH Robinson Registration" backArrow />

      <div className="m-auto my-0 w-full max-w-5xl px-5 pb-24 pt-5">
        <Card title="CH Robinson" draggableProvided>
          <div className="px-5 py-2.5">
            <div className="pb-5">To activate CH Robinson in Shipwell platform, please register your account code.</div>
            <Formik
              enableReinitialize
              initialValues={{c_code: '', ltl: false, ftl: true}}
              onSubmit={handleConnect}
              validationSchema={validationSchema}
            >
              {(innerProps: {
                values: {c_code: string; ltl: boolean; ftl: boolean};
                dirty?: boolean;
                isValid?: boolean;
                isSubmitting?: boolean;
                handleSubmit: FormEventHandler<HTMLFormElement>;
              }) => {
                return (
                  <form noValidate onSubmit={innerProps.handleSubmit}>
                    <Field
                      required
                      name="c_code"
                      label="C Code"
                      component={FormikTextInput}
                      data-testId="c-code-input"
                    />
                    {intCarrierApiChrLtl && (
                      <div className="flex flex-col pt-5">
                        <Title variant="formTitle" className="mb-3">
                          Shipping Mode:
                        </Title>
                        <div className="flex flex-row">
                          <div className="flex w-96 content-start items-start">
                            <Field
                              name="ltl"
                              label="LTL"
                              component={FormikCheckbox}
                              data-testId="ltl-input"
                              fixedHeight={false}
                              validate={() => validateCheckboxes(innerProps.values)}
                            />
                          </div>
                          <div className="flex w-96 content-start items-start">
                            <Field
                              name="ftl"
                              label="FTL"
                              component={FormikCheckbox}
                              data-testId="ftl-input"
                              fixedHeight={false}
                              validate={() => validateCheckboxes(innerProps.values)}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                    {innerProps.dirty && (
                      <FormFooter primaryActionName={'Connect CHRobinson'} onCancel={() => onCancel()} />
                    )}
                  </form>
                );
              }}
            </Formik>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default compose<CHRPropsTypes, unknown>(
  withRouter,
  withFlags('connDigitalFreightMarketplace'),
  WithStatusToasts,
  withConditionalFallback(
    (props: {connDigitalFreightMarketplace?: boolean}) => !props.connDigitalFreightMarketplace,
    () => <p className="text-center">You don&#39;t have access to this integration.</p>
  )
)(CHRobinsonRegistration);
